import React from 'react';
import { useIntl } from 'react-intl';

/**
 * LoadingStates - Beautiful loading components for the messaging system with succession theming
 * 
 * Succession themed loading states with smooth animations.
 * Uses marketplace colors for consistent theming.
 */

const LoadingStates = {
  
  /**
   * ConversationList - Loading state for conversation sidebar
   */
  ConversationList: () => (
    <div className="p-4 space-y-3">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="flex items-center space-x-3 p-3 bg-succession-bg-primary rounded-lg animate-pulse shadow-succession-sm">
          <div className="w-10 h-10 bg-succession-border rounded-full"></div>
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-succession-border rounded w-3/4"></div>
            <div className="h-3 bg-succession-bg-secondary rounded w-1/2"></div>
          </div>
          <div className="h-3 bg-succession-bg-secondary rounded w-8"></div>
        </div>
      ))}
    </div>
  ),

  /**
   * MessageList - Loading state for chat messages
   */
  MessageList: () => (
    <div className="flex-1 p-4 space-y-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className={`flex ${i % 2 === 0 ? 'justify-end' : 'justify-start'}`}>
          <div className={`flex items-start space-x-3 max-w-xs ${i % 2 === 0 ? 'flex-row-reverse space-x-reverse' : ''}`}>
            <div className="w-8 h-8 bg-succession-border rounded-full animate-pulse"></div>
            <div className="space-y-2">
              <div className={`h-4 rounded w-24 animate-pulse ${i % 2 === 0 ? 'bg-succession-primary-light' : 'bg-succession-border'}`}></div>
              <div className={`h-12 rounded-lg animate-pulse ${i % 2 === 0 ? 'bg-succession-primary bg-opacity-20' : 'bg-succession-bg-secondary'}`}></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  ),

  /**
   * Spinner - Simple loading spinner with succession colors
   */
  Spinner: ({ size = 'md', className = '' }) => {
    const sizeClasses = {
      sm: 'w-4 h-4',
      md: 'w-6 h-6',
      lg: 'w-8 h-8',
      xl: 'w-12 h-12'
    };

    return (
      <div className={`${sizeClasses[size]} border-2 border-succession-border border-t-succession-primary rounded-full animate-spin ${className}`}></div>
    );
  },

  /**
   * TypingIndicator - Shows when someone is typing with succession colors
   */
  TypingIndicator: ({ userName = 'Someone' }) => {
    const intl = useIntl();
    return (
      <div className="flex items-center space-x-2 text-sm text-succession-text-secondary p-3">
        <div className="flex space-x-1">
          <div className="w-2 h-2 bg-succession-primary rounded-full animate-bounce"></div>
          <div className="w-2 h-2 bg-succession-primary rounded-full animate-bounce delay-75"></div>
          <div className="w-2 h-2 bg-succession-primary rounded-full animate-bounce delay-150"></div>
        </div>
        <span className="italic">{intl.formatMessage({ id: 'succession.messaging.loading_states.typing_indicator' }, { name: userName })}</span>
      </div>
    );
  },

  /**
   * ChatHeader - Loading state for chat header
   */
  ChatHeader: () => (
    <div className="flex items-center space-x-3 p-4 bg-succession-bg-primary border-b border-succession-border animate-pulse">
      <div className="w-10 h-10 bg-succession-border rounded-full"></div>
      <div className="flex-1 space-y-2">
        <div className="h-4 bg-succession-border rounded w-1/3"></div>
        <div className="h-3 bg-succession-bg-secondary rounded w-1/4"></div>
      </div>
      <div className="flex space-x-2">
        <div className="w-8 h-8 bg-succession-border rounded"></div>
        <div className="w-8 h-8 bg-succession-border rounded"></div>
      </div>
    </div>
  ),

  /**
   * MessageComposer - Loading state for message input
   */
  MessageComposer: () => (
    <div className="p-4 bg-succession-bg-primary border-t border-succession-border animate-pulse">
      <div className="flex items-center space-x-3">
        <div className="w-8 h-8 bg-succession-border rounded-lg"></div>
        <div className="flex-1 h-12 bg-succession-bg-secondary rounded-xl"></div>
        <div className="w-12 h-12 bg-succession-primary bg-opacity-30 rounded-xl"></div>
      </div>
    </div>
  ),

  /**
   * FileAttachment - Loading state for file attachments
   */
  FileAttachment: () => (
    <div className="flex items-center space-x-3 p-3 bg-succession-bg-secondary border border-succession-border rounded-lg animate-pulse">
      <div className="w-10 h-10 bg-succession-border rounded"></div>
      <div className="flex-1 space-y-2">
        <div className="h-4 bg-succession-border rounded w-2/3"></div>
        <div className="h-3 bg-succession-bg-primary rounded w-1/3"></div>
      </div>
      <div className="w-6 h-6 bg-succession-border rounded"></div>
    </div>
  ),

  /**
   * EmptyState - Loading state for empty conversations
   */
  EmptyState: () => (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="text-center animate-pulse">
        <div className="w-16 h-16 bg-succession-border rounded-full mx-auto mb-4"></div>
        <div className="h-6 bg-succession-border rounded w-48 mx-auto mb-2"></div>
        <div className="h-4 bg-succession-bg-secondary rounded w-32 mx-auto"></div>
      </div>
    </div>
  ),

  /**
   * SearchResults - Loading state for search results
   */
  SearchResults: () => (
    <div className="p-4 space-y-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="p-3 bg-succession-bg-primary border border-succession-border rounded-lg animate-pulse">
          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 bg-succession-border rounded-full"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-succession-border rounded w-1/2"></div>
              <div className="h-3 bg-succession-bg-secondary rounded w-3/4"></div>
              <div className="h-3 bg-succession-bg-secondary rounded w-1/3"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
};

export default LoadingStates; 